import {
  IPayerAutoSelectionEvaluateRequest,
  IPayerAutoSelectionEvaluateResult,
  IPayerAutoSelectionOrderRequest,
  IPayerAutoSelectionRule,
  IPayerAutoSelectionRuleSaveRequest,
} from "types/PayerAutoSelection/PayerAutoSelection";

import { api } from ".";

/**
 * CLE-2845 — Payer Auto-Selection rules. SuperAdmin on every endpoint, reads included: a rule can
 * change the payer an estimate is priced under at every facility.
 *
 * The tag is registered here rather than in the shared tagTypes list, so this feature's cache stays
 * in its own file. Every write invalidates the list; the tester is a mutation (a POST that reads), so
 * it is never cached and always runs against the rules as saved.
 *
 * The reorder is OPTIMISTIC: the dragged row stays where it was dropped while the PUT runs, and
 * jumps back if the API refuses (say, another admin added a rule since the tab loaded).
 */
const apiWithPayerAutoSelectionTags = api.enhanceEndpoints({
  addTagTypes: ["PayerAutoSelectionRules"],
});

export const PAYER_AUTO_SELECTION_PATH =
  "eligibility/payer-auto-selection-rules";

export const payerAutoSelectionQueries =
  apiWithPayerAutoSelectionTags.injectEndpoints({
    endpoints: (builder) => ({
      getPayerAutoSelectionRules: builder.query<
        IPayerAutoSelectionRule[],
        void
      >({
        query: () => PAYER_AUTO_SELECTION_PATH,
        transformResponse: (response: { data: IPayerAutoSelectionRule[] }) =>
          response.data,
        providesTags: [{ type: "PayerAutoSelectionRules", id: "LIST" }],
      }),
      createPayerAutoSelectionRule: builder.mutation<
        IPayerAutoSelectionRule,
        IPayerAutoSelectionRuleSaveRequest
      >({
        query: (body) => ({
          url: PAYER_AUTO_SELECTION_PATH,
          method: "POST",
          body,
        }),
        transformResponse: (response: { data: IPayerAutoSelectionRule }) =>
          response.data,
        invalidatesTags: [{ type: "PayerAutoSelectionRules", id: "LIST" }],
      }),
      updatePayerAutoSelectionRule: builder.mutation<
        IPayerAutoSelectionRule,
        { id: string; body: IPayerAutoSelectionRuleSaveRequest }
      >({
        query: ({ id, body }) => ({
          url: `${PAYER_AUTO_SELECTION_PATH}/${encodeURIComponent(id)}`,
          method: "PUT",
          body,
        }),
        transformResponse: (response: { data: IPayerAutoSelectionRule }) =>
          response.data,
        invalidatesTags: [{ type: "PayerAutoSelectionRules", id: "LIST" }],
      }),
      deletePayerAutoSelectionRule: builder.mutation<{ id: string }, string>({
        query: (id) => ({
          url: `${PAYER_AUTO_SELECTION_PATH}/${encodeURIComponent(id)}`,
          method: "DELETE",
        }),
        transformResponse: (response: { data: { id: string } }) =>
          response.data,
        invalidatesTags: [{ type: "PayerAutoSelectionRules", id: "LIST" }],
      }),
      reorderPayerAutoSelectionRules: builder.mutation<
        IPayerAutoSelectionRule[],
        IPayerAutoSelectionOrderRequest
      >({
        query: (body) => ({
          url: `${PAYER_AUTO_SELECTION_PATH}/order`,
          method: "PUT",
          body,
        }),
        transformResponse: (response: { data: IPayerAutoSelectionRule[] }) =>
          response.data,
        async onQueryStarted({ ids }, { dispatch, queryFulfilled }) {
          const patch = dispatch(
            payerAutoSelectionQueries.util.updateQueryData(
              "getPayerAutoSelectionRules",
              undefined,
              (draft) => {
                const byId = new Map(draft.map((rule) => [rule.id, rule]));
                const reordered = ids.flatMap((id) =>
                  byId.has(id) ? [byId.get(id) as IPayerAutoSelectionRule] : []
                );
                return reordered.length === draft.length ? reordered : draft;
              }
            )
          );
          try {
            await queryFulfilled;
          } catch {
            patch.undo();
          }
        },
        invalidatesTags: [{ type: "PayerAutoSelectionRules", id: "LIST" }],
      }),
      evaluatePayerAutoSelection: builder.mutation<
        IPayerAutoSelectionEvaluateResult,
        IPayerAutoSelectionEvaluateRequest
      >({
        query: (body) => ({
          url: `${PAYER_AUTO_SELECTION_PATH}/evaluate`,
          method: "POST",
          body,
        }),
        transformResponse: (response: {
          data: IPayerAutoSelectionEvaluateResult;
        }) => response.data,
      }),
    }),
  });

export const {
  useGetPayerAutoSelectionRulesQuery,
  useCreatePayerAutoSelectionRuleMutation,
  useUpdatePayerAutoSelectionRuleMutation,
  useDeletePayerAutoSelectionRuleMutation,
  useReorderPayerAutoSelectionRulesMutation,
  useEvaluatePayerAutoSelectionMutation,
} = payerAutoSelectionQueries;
